import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect } from "react";

import { useNotificationNamek } from "@/features/notifications";
import { INamekError } from "@/models";
import { DescriptionUnknownError, SaveErrorMessage } from "@/constants/commonMessages";
import { useGetMaterialById } from "@/config/services";
import { useRefetchMaterialsList } from "@/config/hooks/useRefetchMaterialsList";

import { useCreateMaterial } from "../../services/useCreateMaterial";
import { useUpdateMaterial } from "../../services/useUpdateMaterial";

import { materialSchema, MaterialSchemaInfer } from "./MaterialForm.schema";

export const useMaterialForm = ({ id, onDone }: { id: number; onDone: () => void }) => {
  const { toast } = useNotificationNamek();
  const { refetchList } = useRefetchMaterialsList();

  const { data: material, isFetching, error } = useGetMaterialById(id);

  useEffect(() => {
    if (!error) {
      return;
    }

    if (error.status === 404) {
      toast.error("No se puede editar el material", {
        description: "El material no existe o fue eliminado del listado.",
        actionButton: {
          label: "Actualizar listado",
          onClick: refetchList,
        },
      });
    } else {
      toast.error("No se puede editar el asociado", {
        description: DescriptionUnknownError,
      });
    }

    onDone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, onDone]);

  const defaultValue: Partial<MaterialSchemaInfer> = {
    name: "",
  };

  const creator = useCreateMaterial();
  const updator = useUpdateMaterial();

  const form = useForm<MaterialSchemaInfer>({
    resolver: zodResolver(materialSchema),
    defaultValues: defaultValue,
  });

  const isNew = id < 1;
  const isDirty = form.formState.isDirty;
  const isSubmitDisabled = !isDirty && !isNew;

  useEffect(() => {
    if (isNew) {
      return;
    }
    form.reset(material);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [material, isNew]);

  const handleError = (error: INamekError, data: MaterialSchemaInfer) => {
    if (error.status === 409) {
      form.setError("name", {
        message: "El nombre del material ya fue utilizado.",
      });
      return;
    }

    if (error.status === 404) {
      toast.error(SaveErrorMessage.title, {
        description: `El material ${data.name} no existe o fué eliminado del listado.`,
        actionButton: {
          label: "Actualizar listado",
          onClick: refetchList,
        },
      });
      onDone();
      return;
    }

    toast.show({
      ...SaveErrorMessage,
      type: "error",
    });
  };

  const doCreate = (data: MaterialSchemaInfer) => {
    creator.mutate(data, {
      onSuccess: () => {
        toast.success("Material creado", {
          description: "Ya lo puedes visualizar en el listado.",
        });
        onDone();
      },
      onError: (error) => {
        handleError(error, data);
      },
    });
  };

  const doUpdate = (data: MaterialSchemaInfer) => {
    updator.mutate(
      { id, ...data },
      {
        onSuccess: () => {
          toast.success("Cambios guardados");
          onDone();
        },
        onError: (error) => {
          handleError(error, data);
        },
      },
    );
  };

  const onSubmit = useCallback(
    (data: MaterialSchemaInfer) => {
      if (isNew) {
        doCreate(data);
      } else {
        doUpdate(data);
      }
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [isNew],
  );

  return {
    isNew,
    form,
    onSubmit,
    isFetching,
    isSubmiting: creator.isPending || updator.isPending,
    isSubmitDisabled,
  };
};
